import React, { useState, useEffect } from 'react';
import newsletterService from '../services/newsletterService';
import { Newsletter } from '../types/contentManagement';
import ContentModal from './ContentModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const NewsletterManagement: React.FC = () => {
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNewsletter, setEditingNewsletter] = useState<Partial<Newsletter> | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newsletterToDelete, setNewsletterToDelete] = useState<Newsletter | null>(null);
  const [filterFrequency, setFilterFrequency] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const frequencies = ['Monthly', 'Quarterly', 'Yearly'];
  const months = newsletterService.getMonthsList();
  const quarters = newsletterService.getQuartersList();

  useEffect(() => {
    fetchNewsletters();
  }, [filterFrequency]);

  const fetchNewsletters = async () => {
    try {
      setLoading(true);
      const data = filterFrequency 
        ? await newsletterService.getNewslettersByFrequency(filterFrequency, true)
        : await newsletterService.getAllNewsletters(true);
      
      const sortedData = (data || []).sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        if (a.month && b.month) return months.indexOf(b.month) - months.indexOf(a.month);
        return a.title.localeCompare(b.title);
      });
      
      setNewsletters(sortedData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch newsletters');
      console.error('Error fetching newsletters:', err);
      setNewsletters([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    const currentYear = new Date().getFullYear();
    setEditingNewsletter({
      title: '',
      frequency: 'Monthly',
      month: months[0],
      year: currentYear,
      is_visible: true,
      sort_order: newsletters.length + 1
    });
    setSelectedFile(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (newsletter: Newsletter) => {
    setEditingNewsletter({ ...newsletter });
    setSelectedFile(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingNewsletter(null);
    setSelectedFile(null);
  };

  const handleSave = async () => {
    if (!editingNewsletter || !editingNewsletter.title || !editingNewsletter.frequency || !editingNewsletter.year) {
      setSaveMessage('Error: Title, Frequency, and Year are required');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    if (editingNewsletter.frequency !== 'Yearly' && !editingNewsletter.month) {
      setSaveMessage('Error: Month/Quarter is required for Monthly and Quarterly newsletters');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    if (!selectedFile && !editingNewsletter.file_path && !editingNewsletter.link) {
      setSaveMessage('Error: Please provide either a file or a link');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      
      formData.append('title', editingNewsletter.title);
      formData.append('frequency', editingNewsletter.frequency);
      if (editingNewsletter.month) formData.append('month', editingNewsletter.month);
      formData.append('year', String(editingNewsletter.year));
      if (editingNewsletter.link) formData.append('link', editingNewsletter.link);
      formData.append('is_visible', String(editingNewsletter.is_visible ?? true));
      formData.append('sort_order', String(editingNewsletter.sort_order || newsletters.length + 1));
      
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      if (editingNewsletter.id) {
        await newsletterService.updateNewsletter(editingNewsletter.id, formData);
        setSaveMessage('Newsletter saved successfully');
      } else {
        await newsletterService.createNewsletter(formData);
        setSaveMessage('Newsletter created successfully');
      }
      
      await fetchNewsletters();
      handleCloseModal();
      setCurrentPage(1);
      
      setTimeout(() => setSaveMessage(''), 3000);
      window.dispatchEvent(new Event('contentUpdated'));
    } catch (err) {
      setSaveMessage('Error saving newsletter');
      console.error('Error saving newsletter:', err);
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (newsletter: Newsletter) => {
    setNewsletterToDelete(newsletter);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!newsletterToDelete) return;

    try {
      setSubmitting(true);
      await newsletterService.deleteNewsletter(newsletterToDelete.id);
      await fetchNewsletters();
      setCurrentPage(1);
      setSaveMessage('Newsletter deleted successfully');
      setTimeout(() => setSaveMessage(''), 3000);
      window.dispatchEvent(new Event('contentUpdated'));
    } catch (err) {
      setSaveMessage('Error deleting newsletter');
      console.error('Error deleting newsletter:', err);
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setSubmitting(false);
      setNewsletterToDelete(null);
    }
  };

  const handleToggleVisibility = async (id: number) => {
    try {
      await newsletterService.toggleVisibility(id);
      await fetchNewsletters();
      window.dispatchEvent(new Event('contentUpdated'));
    } catch (err) {
      console.error('Error toggling visibility:', err);
    }
  };

  const filteredNewsletters = newsletters.filter(newsletter => {
    const query = searchQuery.toLowerCase();
    return newsletter.title.toLowerCase().includes(query) ||
           newsletter.frequency.toLowerCase().includes(query) ||
           (newsletter.month && newsletter.month.toLowerCase().includes(query)) ||
           newsletter.year.toString().includes(query);
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentNewsletters = filteredNewsletters.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredNewsletters.length / itemsPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const getYearOptions = () => {
    const years = [];
    for (let i = 2026; i <= 2050; i++) {
      years.push(i);
    }
    return years;
  };

  const getPeriodOptions = () => {
    if (!editingNewsletter?.frequency) return [];
    
    if (editingNewsletter.frequency === 'Monthly') {
      return months;
    } else if (editingNewsletter.frequency === 'Quarterly') {
      return quarters;
    }
    return [];
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading newsletters...</div>;
  }

  return (
    <div className="edit-section">
      <div className="applications-list">
        <div className="list-header">
          <h4>Newsletter Management</h4>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select 
              value={filterFrequency} 
              onChange={(e) => setFilterFrequency(e.target.value)}
              className="filter-select"
            >
              <option value="">All Frequencies</option>
              {frequencies.map(freq => (
                <option key={freq} value={freq}>{freq}</option>
              ))}
            </select>
            <button className="add-btn" onClick={handleAddClick}>
              Add Newsletter
            </button>
          </div>
        </div>

        {saveMessage && (
          <div className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`}>
            {saveMessage}
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="search-box" style={{ marginBottom: '20px' }}>
          <input
            type="text"
            placeholder="Search by title..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="search-input"
          />
        </div>

        <div className="table-container">
          <table className="content-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Frequency</th>
                <th>Period</th>
                <th>Year</th>
                <th>Active</th>
                <th>Order</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentNewsletters.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '40px' }}>
                    No newsletters found. Click "Add Newsletter" to create one.
                  </td>
                </tr>
              ) : (
                currentNewsletters.map((newsletter) => (
                  <tr key={newsletter.id}>
                    <td>{newsletter.title}</td>
                    <td>
                      <span 
                        className="category-badge" 
                        style={{ 
                          backgroundColor: newsletterService.getFrequencyColor(newsletter.frequency),
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}
                      >
                        {newsletter.frequency}
                      </span>
                    </td>
                    <td>{newsletter.month || '-'}</td>
                    <td>{newsletter.year}</td>
                    <td>
                      <span 
                        className="status-badge"
                        style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: newsletter.is_visible ? '#d4edda' : '#f8d7da',
                          color: newsletter.is_visible ? '#155724' : '#721c24'
                        }}
                      >
                        {newsletter.is_visible ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{newsletter.sort_order}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          onClick={() => handleEditClick(newsletter)}
                          className="edit-btn"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button 
                          onClick={() => handleDeleteClick(newsletter)}
                          className="delete-btn"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              >
                {page}
              </button>
            ))}
            
            <button 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {isModalOpen && editingNewsletter && (
        <ContentModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={editingNewsletter.id ? 'Edit Newsletter' : 'Add Newsletter'}
        >
          <div className="modal-form">
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={editingNewsletter.title || ''}
                onChange={(e) => setEditingNewsletter({ ...editingNewsletter, title: e.target.value })}
                placeholder="Enter newsletter title"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Frequency *</label>
              <select
                value={editingNewsletter.frequency || 'Monthly'}
                onChange={(e) => setEditingNewsletter({ 
                  ...editingNewsletter, 
                  frequency: e.target.value as 'Monthly' | 'Quarterly' | 'Yearly',
                  month: e.target.value === 'Yearly' ? undefined : (e.target.value === 'Quarterly' ? quarters[0] : months[0])
                })}
                className="form-input"
              >
                {frequencies.map(freq => (
                  <option key={freq} value={freq}>{freq}</option>
                ))}
              </select>
            </div>

            {editingNewsletter.frequency !== 'Yearly' && (
              <div className="form-group">
                <label>{editingNewsletter.frequency === 'Quarterly' ? 'Quarter' : 'Month'} *</label>
                <select
                  value={editingNewsletter.month || ''}
                  onChange={(e) => setEditingNewsletter({ ...editingNewsletter, month: e.target.value })}
                  className="form-input"
                >
                  {getPeriodOptions().map(period => (
                    <option key={period} value={period}>{period}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Year *</label>
              <select
                value={editingNewsletter.year || new Date().getFullYear()}
                onChange={(e) => setEditingNewsletter({ ...editingNewsletter, year: parseInt(e.target.value) })}
                className="form-input"
              >
                {getYearOptions().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>File (PDF)</label>
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="form-input"
              />
              {editingNewsletter.file_path && !selectedFile && (
                <div className="current-file">
                  Current file: <a href={newsletterService.getFileUrl(editingNewsletter.file_path)} target="_blank" rel="noopener noreferrer">View PDF</a>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Or Link</label>
              <input
                type="url"
                value={editingNewsletter.link || ''}
                onChange={(e) => setEditingNewsletter({ ...editingNewsletter, link: e.target.value })}
                placeholder="https://example.com/newsletter.pdf"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={editingNewsletter.is_visible ?? true}
                  onChange={(e) => setEditingNewsletter({ ...editingNewsletter, is_visible: e.target.checked })}
                />
                <span style={{ marginLeft: '8px' }}>Visible on homepage</span>
              </label>
            </div>

            <div className="form-group">
              <label>Sort Order</label>
              <input
                type="number"
                value={editingNewsletter.sort_order || 0}
                onChange={(e) => setEditingNewsletter({ ...editingNewsletter, sort_order: parseInt(e.target.value) })}
                className="form-input"
                min="0"
              />
            </div>

            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button 
                onClick={handleCloseModal} 
                className="btn-cancel" 
                disabled={submitting}
                style={{
                  padding: '10px 24px',
                  border: '1px solid #ddd',
                  background: 'white',
                  color: '#666',
                  borderRadius: '4px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleSave} 
                className="btn-save" 
                disabled={submitting}
                style={{
                  padding: '10px 24px',
                  border: 'none',
                  background: '#00a0e3',
                  color: 'white',
                  borderRadius: '4px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </ContentModal>
      )}

      {showDeleteModal && newsletterToDelete && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setNewsletterToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          itemName={newsletterToDelete.title}
        />
      )}
    </div>
  );
};

export default NewsletterManagement;
